import React, { useEffect, useMemo, useState, useContext, useCallback, useRef } from 'react';
import { Button, Col, Drawer, Image, Popconfirm, Row, Space, Table, Typography, Card, Tooltip } from 'antd';
import { openNotification } from '../../../utils';
import { HolderOutlined } from '@ant-design/icons';
import { CheckmarkCircle02Icon } from 'hugeicons-react';
import { DndContext, DragOverlay } from '@dnd-kit/core';
import type { DragEndEvent } from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { SortableContext, arrayMove, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import TrashIcon from 'assets/icons/trash.svg';
import PenIcon from 'assets/icons/pen.svg';

import { createStandardAnswers, listAnswers, listAnswersAll, deleteAnswer, patchAnswer, reorderAnswers } from '../services/answerService';
import AnswerForm from './Form';

type AnswerListProps = {
  referenceId?: number;
  questionType?: string | null;
  referenceType?: 'question';
};

const countAnswerImageFields = (value: any): number => {
  let parsed = value;
  if (typeof value === 'string') {
    if (!value.trim()) {
      return 0;
    }
    try {
      parsed = JSON.parse(value);
    } catch {
      return 0;
    }
  }
  if (!Array.isArray(parsed)) {
    return 0;
  }
  return parsed.filter((item: any) => String(item?.title ?? '').trim().length > 0).length;
};

const STANDARD_ANSWER_TITLES = new Set(['UYGUN', 'UYGUN DEĞİL', 'KAPSAM DIŞI']);

const normalizeAnswerTitle = (title: unknown): string => String(title ?? '')
  .trim()
  .replace(/\s+/g, ' ')
  .toLocaleUpperCase('tr-TR');

export default function AnswerList({ referenceId, questionType, referenceType = 'question' }: AnswerListProps){
  const [data, setData] = useState<any[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | undefined>();
  const [creatingStandardAnswers, setCreatingStandardAnswers] = useState(false);
  
  const [activeId, setActiveId] = useState<string | null>(null);

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const requestSequence = useRef(0);
  const isQuestionAnswerList = referenceId !== undefined;
  const fetchData = useCallback(async () => {
    const requestId = ++requestSequence.current;
    setLoading(true);
    setLoadError(false);
    setData([]);
    setTotal(0);
    try {
      if (requestId !== requestSequence.current) return;
      if (isQuestionAnswerList) {
        const answers = await listAnswersAll('questionId.equals=' + referenceId, 'sortOrder,asc');
        if (requestId !== requestSequence.current) return;
        setData(answers);
        setTotal(answers.length);
      } else {
        const res = await listAnswers(page - 1, pageSize, 'sortOrder,asc', '');
        if (requestId !== requestSequence.current) return;
        setData(res.content);
        setTotal(res.totalElements);
      }
    } catch {
      if (requestId === requestSequence.current) setLoadError(true);
    } finally {
      if (requestId === requestSequence.current) setLoading(false);
    }
  }, [isQuestionAnswerList, page, pageSize, referenceId, referenceType]);
  useEffect(() => {
    fetchData();
    return () => { requestSequence.current += 1; };
  }, [fetchData]);
  const openCreate = () => { setEditingId(undefined); setOpen(true); };
  const openEdit = (id:number) => { setEditingId(id); setOpen(true); };
  const handleClose = () => setOpen(false);
  const handleMakeDefault = async (id: number) => {
    try {
      await patchAnswer(id, { isDefault: true });
      openNotification('success', 'Başarılı', 'Varsayılan olarak işaretlendi');
      fetchData();
    } catch (e) {
      openNotification('error', 'Hata', 'Varsayılan ayarlanamadı');
    }
  };

  const handleCreateStandardAnswers = async () => {
    if (referenceId === undefined) return;
    setCreatingStandardAnswers(true);
    try {
      const response = await createStandardAnswers(referenceId);
      if (response.created) {
        openNotification('success', 'Başarılı', `${response.createdCount} standart cevap eklendi.`);
      } else {
        openNotification('info', 'Bilgi', 'Standart cevap zaten bulunduğu için işlem yapılmadı.');
      }
      fetchData();
    } catch (error) {
      const message = error instanceof Error && error.message ? error.message : 'Standart cevaplar eklenemedi.';
      openNotification('error', 'Hata', message);
    } finally {
      setCreatingStandardAnswers(false);
    }
  };

  // Drag & drop ordering
  // DnD Kit setup similar to ParameterList
  const RowContext = React.createContext<{ setActivatorNodeRef?: (el: HTMLElement | null) => void; listeners?: any }>({});
  const DragHandle: React.FC = () => {
    const { setActivatorNodeRef, listeners } = useContext(RowContext);
    return (
      <HolderOutlined ref={setActivatorNodeRef} {...listeners} style={{ cursor: 'move', color: '#999' }} />
    );
  };
  const handleDelete = async (id:number) => {
    try {
      await deleteAnswer(id);
      openNotification('success', 'Başarılı', `${answerLabel} Silindi`);
      fetchData();
    } catch (e) {
      openNotification('error', 'Hata', `${answerLabel} silme işlemi başarısız`);
    }
  };
  const RowComponent = (props: any) => {
    const { children, ...restProps } = props;
    const record = children?.[0]?.props?.record || {};
    const key = String(record?.id ?? restProps['data-row-key'] ?? 'no-key');

    const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({ id: key });
    const style: React.CSSProperties = {
      ...restProps.style,
      transform: CSS.Transform.toString(transform),
      transition,
      ...(isDragging ? { position: 'relative', zIndex: 9999 } : {}),
    };

    const value = useMemo(() => ({ setActivatorNodeRef, listeners }), [setActivatorNodeRef, listeners]);

    return (
      <RowContext.Provider value={value}>
        <tr {...restProps} ref={setNodeRef} style={style} {...attributes}>
          {children}
        </tr>
      </RowContext.Provider>
    );
  };

  const onDragEnd = async ({ active, over }: DragEndEvent) => {
    setActiveId(null);
    if (!over || active.id === over.id) return;
    const oldIndex = data.findIndex((item: any) => String(item.id) === String(active.id));
    const newIndex = data.findIndex((item: any) => String(item.id) === String(over.id));
    if (oldIndex === -1 || newIndex === -1) return;
    const newData = arrayMove(data, oldIndex, newIndex);
    // Recalculate sortOrder starting from 1
    const reordered = newData.map((item: any, index: number) => ({ id: item.id, sortOrder: index + 1 }));
    setData((prev) => newData.map((item: any, index: number) => ({ ...item, sortOrder: index + 1 })));
    try {
      await reorderAnswers(reordered);
      openNotification('success', 'Başarılı', 'Sıralama güncellendi');
    } catch (e) {
      openNotification('error', 'Hata', 'Sıralama güncellenemedi');
      fetchData();
    }
  };

  const sortableItems = useMemo(() => data.map((d: any) => String(d.id)), [data]);

  const normalizedQuestionType = useMemo(() => {
    if (questionType === undefined || questionType === null) {
      return undefined;
    }
    return questionType.toString().toUpperCase();
  }, [questionType]);

  const showFunctionColumn = !normalizedQuestionType || normalizedQuestionType === 'FUNCTION';
  const showImageColumn = normalizedQuestionType === 'MULTIPLE';
  const answerLabel = normalizedQuestionType === 'FUNCTION' ? 'Koşul' : 'Cevap';
  const hasStandardAnswer = useMemo(
    () => data.some((answer) => STANDARD_ANSWER_TITLES.has(normalizeAnswerTitle(answer?.title))),
    [data],
  );

  const columns = [
    {
      key: 'drag',
      width: 40,
      align: 'center' as const,
      render: () => <DragHandle />,
    },
    { title: 'Raporda Görünümü', dataIndex: 'abbreviation', sorter: true },
    { title: 'Başlık', dataIndex: 'title', sorter: true },
    ...(showFunctionColumn
      ? [{
        title: 'Fonksiyon Metni',
        dataIndex: 'functionText',
        render: (value: string, record: any) =>
          normalizedQuestionType === 'FUNCTION' || (record?.question?.questionType ?? '').toString().toUpperCase() === 'FUNCTION'
            ? value
            : '',
      }]
      : []),
    ...(showImageColumn
      ? [{
        title: 'Cevap Görseli',
        dataIndex: 'imageUrl',
        align: 'center' as const,
        render: (value: string) =>
          value ? (
            <Image
              src={value}
              alt='Cevap görseli'
              width={48}
              height={36}
              style={{ objectFit: 'cover', borderRadius: 4 }}
            />
          ) : '',
      }, {
        title: 'Görsel',
        dataIndex: 'imageFields',
        align: 'center' as const,
        render: (value: any) => {
          const count = countAnswerImageFields(value);
          return count > 0 ? count : '';
        },
      }]
      : []),
    { title: 'Varsayılan', dataIndex: 'isDefault', sorter: true, render: (v: boolean, r: any) => (
      v ? (
        <CheckmarkCircle02Icon style={{ color: '#52c41a', fontSize: 18 }} />
      ) : (
        <Tooltip title='Varsayılan Yap'>
          <span onClick={() => handleMakeDefault(r.id)} style={{ cursor: 'pointer' }}>
            <CheckmarkCircle02Icon style={{ color: '#bfbfbf', fontSize: 18 }} />
          </span>
        </Tooltip>
      )
    ) },
    { title: 'Doğru', dataIndex: 'isTrue', sorter: true, render: (v: boolean) => v ? <CheckmarkCircle02Icon style={{ color: '#52c41a', fontSize: 18 }} /> : '' },
    { title: 'İşlemler', render: (_:any, r:any) => (
      <Space>
        <Tooltip title='Düzenle'>
          <Button
            icon={<img src={PenIcon} alt="pen" className="icon" />}
            shape="circle"
            onClick={() => openEdit(r.id)}
            className="icon-button"
          />
        </Tooltip>
        <Popconfirm title='Sil?' onConfirm={() => handleDelete(r.id)}>
          <Tooltip title='Sil'>
            <Button
              icon={<img src={TrashIcon} alt="trash" className="icon" />}
              shape="circle"
              className="icon-button ml-12px"
            />
          </Tooltip>
        </Popconfirm>
      </Space>
    ) }
  ];
  return (
    <>
      
      <Card
        className='br-30px'
        title={<Typography.Title level={4} style={{ margin: 0 }}>{answerLabel}</Typography.Title>}
        extra={
          <Space>
            {normalizedQuestionType === 'MULTIPLE' && referenceId !== undefined ? (
              <Tooltip title={hasStandardAnswer ? 'Standart cevap zaten bulunuyor.' : undefined}>
                <span>
                  <Popconfirm
                    title='Standart cevaplar eklensin mi?'
                    description='UYGUN, UYGUN DEĞİL ve KAPSAM DIŞI cevapları eklenecek.'
                    okText='Ekle'
                    cancelText='Vazgeç'
                    onConfirm={handleCreateStandardAnswers}
                    disabled={creatingStandardAnswers}
                  >
                    <Button loading={creatingStandardAnswers}>Standart Cevap Ekle</Button>
                  </Popconfirm>
                </span>
              </Tooltip>
            ) : null}
            <Button type='primary' onClick={openCreate}>Ekle</Button>
          </Space>
        }
      >
        <DndContext
          modifiers={[restrictToVerticalAxis]}
          onDragEnd={onDragEnd}
          onDragStart={({ active }: any) => setActiveId(String(active.id))}
        >
          <SortableContext items={sortableItems} strategy={verticalListSortingStrategy}>
            <Table
              size='small'
              className='table-compact'
              rowKey='id'
              dataSource={data}
              loading={loading}
              locale={{ emptyText: loading ? 'Cevaplar yükleniyor…' : loadError ? (
                <Space direction='vertical'>
                  <span>Cevaplar yüklenemedi.</span>
                  <Button onClick={fetchData}>Tekrar Dene</Button>
                </Space>
              ) : 'Veri Yok' }}
              columns={columns}
              pagination={isQuestionAnswerList ? false : { current: page, pageSize, total, onChange:(p,s)=>{setPage(p); setPageSize(s);} }}
              components={{ body: { row: RowComponent } }}
            />
          </SortableContext>
          <DragOverlay>
            {activeId ? (
              <div style={{ padding: '4px 8px', background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.1)', borderRadius: 4 }}>
                {data.find((d:any) => String(d.id) === String(activeId))?.title}
              </div>
            ) : null}
          </DragOverlay>
        </DndContext>
      </Card>
      <Drawer open={open} onClose={handleClose} destroyOnClose width={480} title={editingId ? `${answerLabel} Düzenle` : `${answerLabel} Oluştur`}>
        <AnswerForm
          id={editingId}
          onSuccess={() => { handleClose(); fetchData();}}
          referenceId={referenceId}
          referenceType={referenceType}
          questionType={questionType}
        />
      </Drawer>
    </>
  );
}
